/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/
import { useEffect, useMemo } from "react";
import moment from "moment";
import PropTypes from 'prop-types';

// @mui material components
// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Switch from "@mui/material/Switch";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";

// Soft UI Dashboard PRO React example components
import DashboardLayout from "nextus/Dashboards/DashboardLayout";
import DashboardNavbar from "nextus/Dashboards/DashboardNavbar";
import Footer from "examples/Footer";

// Calendar application components
import Header from "nextus/Calendars/ClearwayCalendar/components/Header";
import NextClearwayEvents from "nextus/Calendars/ClearwayCalendar/components/NextClearwayEvents";

// Data
import { useNavigate, useMatch } from 'react-router-dom';


import { QueryClient, QueryClientProvider, useQuery, useMutation } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import clearwaysAPI from 'modules/clearways/api';
import groupsAPI from 'api/groups';
import bksAPI from 'api/bks';
import userAPI from "api/user";

import EventForm from "modules/clearways/components/EventForm";


import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import { Box } from "@mui/material";
import GroupMap from "nextus/Maps/GroupMap";

const mapbox_settings = {
    style: 'mapbox://styles/harrymontano/cm01omrnz000u01omf2uegk93?optimize=true',
    zoom: 12,
    // maxZoom: 5
}

function wrapper(form, map) {
    return (
        <SoftBox sx={{ height: '100%', width: '100%' }}>
            <Grid container sx={{ height: '100%', width: '100%' }}>
                <Grid item xxs={12} sx={{ height: '100%', width: '100%', }}>
                    <SoftBox sx={({ boxShadows: { navbarBoxShadow }, borders: { borderRadius }, palette: { white }, functions: { rgba, pxToRem } }) => ({
                        boxShadow: navbarBoxShadow,
                        backdropFilter: `saturate(200%) blur(${pxToRem(4)})`,
                        borderRadius: borderRadius.xl,
                        height: '100%',
                        width: `100%`,
                        overflowY: 'auto',
                        '::-webkit-scrollbar': {
                            backgroundColor: 'transparent',
                            width: '8px',
                        },
                        '::-webkit-scrollbar-track': { /* background of the scrollbar except button or resizer */
                            backgroundColor: 'transparent',
                        },
                        '::-webkit-scrollbar-thumb': { /* scrollbar itself */
                            backgroundColor: '#babac0',
                            borderRadius: '6px',
                            border: `1px solid ${rgba(white.main, 0.8)}`,
                        },
                        '::-webkit-scrollbar-button': { /* set button(top and bottom of the scrollbar) */
                            display: 'none',
                        },
                    })}>
                        {form}
                    </SoftBox>
                </Grid>
            </Grid>
        </SoftBox>
    );
}

function createEvent(data) {
    console.log(data);
    console.log("TO DO... CREATE");
    clearwaysAPI.createClearwayEvent(data);
}

function ClearwaysAddEvent({ map, navTitle, setNavTitle, sidebarState, setSidebarState }) {

    // console.log(route)

    bksAPI.loadToken(useAuth0());
    groupsAPI.loadToken(useAuth0());
    userAPI.loadToken(useAuth0());
    
    const { user } = useAuth0();

    var event = {
        display_time: moment().add(1, 'hour').startOf('hour').format(),
        start_time: moment().add(5, 'day').add(1, 'hour').startOf('hour').format(),
        end_time: moment().add(5, 'day').add(4, 'hour').startOf('hour').format(),
        organisation_id: "clearways",
        is_multi_day: false,
        groups: [],
        cameras_enabled: false
    };

    const groups = useQuery({
        queryKey: ['getGroups', 0],
        queryFn: async () => {
            return await groupsAPI.getAllGroups();
        },
        staleTime: 20 * 1000
    });

    const devices = useQuery({
        queryKey: ['getAllDevices'],
        queryFn: async () => {
            return await bksAPI.getAllDevices();
        }
    });

    const selected_user = useQuery({
        queryKey: ['getUserByEmail', user.email.toLowerCase()],
        queryFn: async () => {
            return await userAPI.getUserByEmail(user.email.toLowerCase());
        }
    });

    // console.log(groups);

    const { mutate } = useMutation({
        mutationFn: (values) => createEvent(values),
    })

    useEffect(() => {
        setSidebarState('sidenav');
        setNavTitle(['clearways', 'new event']);
    }, [sidebarState, navTitle]);

    if (groups.isLoading || devices.isLoading || selected_user.isLoading) {
        return wrapper("Loading", "Loading");
    }

    if (groups.error || devices.error || selected_user.error) {
        return wrapper("Error!", "Error!");
    }

    if (groups.data && devices.data && selected_user.data) {
        // return wrapper((<EventForm event={event} groups={groups.data} devices={devices.data} navTitle={navTitle} setNavTitle={setNavTitle} onSubmit={mutate} isAdd={true} />),(<GroupMap groups={groups.data} settings={mapbox_settings} mapPadding={20} />));
        return wrapper(<EventForm event={event} groups={groups.data} devices={devices.data} navTitle={navTitle} setNavTitle={setNavTitle} onSubmit={mutate} isAdd={true} user_role={selected_user.data.user.user_type} />);
    }

    return wrapper("Loading...");
}

ClearwaysAddEvent.propTypes = {
    map: PropTypes.object,
    navTitle: PropTypes.array,
    setNavTitle: PropTypes.func,
    sidebarState: PropTypes.string,
    setSidebarState: PropTypes.func,
}

export default ClearwaysAddEvent;